
import { useState, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calculator, Shield, Zap } from "lucide-react";
import { trackApplyNowClick, trackPhoneClick } from "@/utils/analytics";
import heroCashDiscount from "@/assets/hero-cash-discount-ultra-optimized.webp";
const ApplyNowModal = lazy(() => import("@/components/ApplyNowModal"));


const Hero = () => {
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  const scrollToCalculator = () => {
    setTimeout(() => {
      document.getElementById('fee-calculator')?.scrollIntoView({ 
        behavior: 'smooth' 
      });
    }, 100);
  };

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden min-h-screen flex items-center" aria-labelledby="hero-heading">
      {/* Hero Background Image */}
      <img 
        src={heroCashDiscount}
        alt=""
        aria-hidden="true"
        loading="eager"
        fetchPriority="high"
        width="1280"
        height="720"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -10
        }}
        className="hero-bg-image absolute inset-0 w-full h-full object-cover -z-10"
      />
      
      {/* Enhanced Overlay for Content Readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/75 to-indigo-900/80"></div>
      
      {/* Animated Particles Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-cyan-400 rounded-full animate-pulse delay-300"></div>
        <div className="absolute top-1/2 left-3/4 w-1 h-1 bg-indigo-400 rounded-full animate-pulse delay-700"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10 w-full">
        <div className="text-center max-w-5xl mx-auto">
          {/* Problem-Oriented Main Headline - Merchant Advocate Persona */}
          <h1 id="hero-heading" className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in leading-tight">
            Eliminate 95% of Credit Card Processing Fees
          </h1>
          
          {/* Value Proposition Subheadline */}
          <p className="text-body-lg md:text-xl text-gray-100 mb-4 leading-relaxed drop-shadow-md animate-fade-in max-w-3xl mx-auto">
            South Florida's trusted merchant advocate since 2024. We've helped 10,000+ businesses save thousands with compliant cash discount programs.
          </p>
          
          <p className="text-lg md:text-xl font-semibold text-accent mb-4 animate-fade-in">
            Average savings: $8,000+ annually · Setup in 48 hours
          </p>
          <p className="text-sm text-[hsl(280,30%,47%)] font-medium mb-2 animate-fade-in">
            ✓ No contracts · No hidden fees · Cancel anytime
          </p>
          <p className="text-sm text-yellow-300 mb-12 animate-fade-in">
            🔥 Limited spots available for new merchants this month · Free rate analysis — usually $299, free this month
          </p>

          {/* Enhanced Key Benefits */}
          <h2 className="sr-only">Key Benefits</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16" role="list">
            <div className="glass-card p-8 hover:bg-white/20 transition-all duration-500 hover-scale group" role="listitem">
              <div className="bg-blue-500/30 backdrop-blur-sm p-4 rounded-2xl w-fit mx-auto mb-6 border border-blue-300/40 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-8 w-8 text-blue-300" />
              </div>
              <h3 className="text-title font-semibold text-white mb-4">Fully Compliant Cash Discount Program</h3>
              <p className="text-body text-gray-200 leading-relaxed">Legal cash discount program that follows all Dodd-Frank regulations and card network rules</p>
            </div>
            <div className="glass-card p-8 hover:bg-white/20 transition-all duration-500 hover-scale group" role="listitem">
              <div className="bg-green-500/30 backdrop-blur-sm p-4 rounded-2xl w-fit mx-auto mb-6 border border-green-300/40 group-hover:scale-110 transition-transform duration-300">
                <Calculator className="h-8 w-8 text-green-300" />
              </div>
              <h3 className="text-title font-semibold text-white mb-4">Transparent Merchant Services Pricing</h3>
              <p className="text-body text-gray-200 leading-relaxed">No hidden fees, no surprises, just honest credit card processing pricing</p>
            </div>
            <div className="glass-card p-8 hover:bg-white/20 transition-all duration-500 hover-scale group" role="listitem">
              <div className="bg-purple-500/30 backdrop-blur-sm p-4 rounded-2xl w-fit mx-auto mb-6 border border-purple-300/40 group-hover:scale-110 transition-transform duration-300">
                <Zap className="h-8 w-8 text-purple-300" />
              </div>
              <h3 className="text-title font-semibold text-white mb-4">Fast Merchant Account Approval</h3>
              <p className="text-body text-gray-200 leading-relaxed">Most applications approved within 1-5 business days with dedicated account management</p>
            </div>
          </div>

          {/* Primary CTA - Conversion Focused */}
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-16">
            <Button 
              size="lg" 
              variant="premium"
              className="min-h-[48px] w-full sm:w-auto px-8 py-4"
              onClick={() => { trackApplyNowClick("Hero"); setIsApplyModalOpen(true); }}
            >
              Get Your Free Savings Analysis
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
            <Button 
              variant="hero" 
              size="lg"
              onClick={scrollToCalculator}
              className="min-h-[48px] w-full sm:w-auto px-8 py-4"
            >
              <Calculator className="mr-3 h-6 w-6" />
              Calculate Your Savings
            </Button>
          </div>
          
          {/* Inline Mini Calculator */}
          <div className="max-w-xl mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 mb-16 animate-fade-in">
            <p className="text-white font-semibold mb-4 text-lg">Quick Savings Estimate</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-gray-300 text-sm mb-1 block">Monthly Volume ($)</label>
                <input
                  type="number"
                  id="hero-volume"
                  defaultValue={30000}
                  className="w-full rounded-lg bg-white/20 border border-white/30 text-white px-4 py-2 text-sm placeholder:text-gray-400"
                  onChange={(e) => {
                    const vol = parseFloat(e.target.value) || 0;
                    const rateEl = document.getElementById('hero-rate') as HTMLInputElement;
                    const rate = parseFloat(rateEl?.value || '3.5') / 100;
                    const resultEl = document.getElementById('hero-result');
                    if (resultEl) resultEl.textContent = `$${Math.round(vol * rate * 0.95 * 12).toLocaleString()}`;
                  }}
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm mb-1 block">Current Rate (%)</label>
                <input
                  type="number"
                  id="hero-rate"
                  defaultValue={3.5}
                  step={0.1}
                  className="w-full rounded-lg bg-white/20 border border-white/30 text-white px-4 py-2 text-sm placeholder:text-gray-400"
                  onChange={(e) => {
                    const rate = (parseFloat(e.target.value) || 0) / 100;
                    const volEl = document.getElementById('hero-volume') as HTMLInputElement;
                    const vol = parseFloat(volEl?.value || '30000');
                    const resultEl = document.getElementById('hero-result');
                    if (resultEl) resultEl.textContent = `$${Math.round(vol * rate * 0.95 * 12).toLocaleString()}`;
                  }}
                />
              </div>
            </div>
            <p className="text-white text-center">
              Estimated annual savings: <span id="hero-result" className="text-2xl font-bold text-green-300">$11,970</span>
            </p>
            <a href="/free-rate-analysis" className="block mt-4 text-center bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 rounded-lg transition-colors">
              Get Your Free Analysis Now →
            </a>
          </div>
          {/* Enhanced Statistics */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="glass-card p-6 text-white hover-scale group">
                <p className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">10,000+</p>
                <p className="text-body text-gray-200 font-medium">Businesses served since 2024</p>
              </div>
              <div className="glass-card p-6 text-white hover-scale group">
                <p className="text-4xl font-bold mb-2 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">4.9/5</p>
                <p className="text-body text-gray-200 font-medium">Avg. satisfaction</p>
              </div>
              <div className="glass-card p-6 text-white hover-scale group">
                <p className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent">PCI Level 1</p>
                <p className="text-body text-gray-200 font-medium">Bank‑grade security</p>
              </div>
            </div>
          </div>

          {/* Enhanced Trust Indicators */}
          <div className="text-body text-gray-200 space-y-6">
            <div className="flex flex-wrap justify-center gap-4">
              <span className="glass px-4 py-2 rounded-full border border-white/30 hover-scale">✅ No Setup Fees</span>
              <span className="glass px-4 py-2 rounded-full border border-white/30 hover-scale">✅ No Contracts</span>
              <span className="glass px-4 py-2 rounded-full border border-white/30 hover-scale">✅ 24/7 Support</span>
              <span className="glass px-4 py-2 rounded-full border border-white/30 hover-scale">✅ Fast Approval</span>
            </div>
            <p className="text-lg">
              Questions? Call us at{" "}
              <a href="tel:9543992230" className="text-cyan-300 hover:text-white hover:underline font-semibold transition-all duration-300 hover-scale inline-block" onClick={() => trackPhoneClick("hero")}>
                (954) 399-2230
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced Decorative Elements */}
      <div className="absolute top-10 left-10 w-24 h-24 bg-blue-400/20 rounded-full opacity-30 animate-float"></div>
      <div className="absolute bottom-10 right-10 w-36 h-36 bg-purple-400/20 rounded-full opacity-30 animate-float delay-1000"></div>
      <div className="absolute top-1/2 right-20 w-16 h-16 bg-cyan-400/20 rounded-full opacity-30 animate-float delay-500"></div>
      <div className="absolute bottom-1/4 left-20 w-12 h-12 bg-indigo-400/20 rounded-full opacity-30 animate-float delay-700"></div>

      <Suspense fallback={null}>
        <ApplyNowModal open={isApplyModalOpen} onOpenChange={setIsApplyModalOpen} />
      </Suspense>
    </section>
  );
};

export default Hero;
